import { ReactNode, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { CaretDown } from '@phosphor-icons/react';
import { Input, Checkbox } from 'antd';
import { ClipLoader } from 'react-spinners';
import { AxiosResponse, HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import useApi from '../../../../utils/AxiosWrapper';
import { useUser } from '../../../../utils/UserContext';
import { useScreenView } from '../../../../utils/ScreenViewContext';
import SignupFooter from '../../SignupFooter';
import {
  Entity,
  ApplicationStep,
  ApplicationStatus,
  CustomerWithDriverOnboarding,
} from '../../../../types/apiTypes';
import { setApplicationStep } from '../../../../pages/signup';
import { Application } from '../../../../types/dbTypes';
import MsaContractText, { MSA_VERSION } from './MsaContractText';
import BankAgreementText, { BANK_AGREEMENT_VERSION } from './BankAgreementText';

interface AgreementsProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
  application: Application;
}

export type AgreementsFormData = {
  signatureFullName: string;
  msaChecked: boolean;
  bankChecked: boolean;
};

/**
 * Nothing on this step is persisted to sessionStorage, deliberately.
 *
 * The consents cannot be: a checkbox already ticked when the page loads is not an affirmative act
 * by the customer, and affirmative assent is the whole basis on which this page binds anyone.
 *
 * The signature is not either, which is the less obvious half. sessionStorage survives logout in
 * the same tab, and nothing sweeps this step's key — so on a shared machine the restore would put
 * one customer's legal name into another customer's signature field, and because a restore runs
 * before the owner prefill, their own name would never appear. The name is prefilled from the
 * owner record anyway; all persistence bought was surviving a reload with a manual correction
 * intact, which is not worth that.
 */
const SAVE_FAILED_MESSAGE =
  'We could not save your agreements. Please try again, or email support@tachusa.com if this issue persists.';

const wasSigned = (response: AxiosResponse | null): boolean =>
  response?.status === HttpStatusCode.Ok ||
  response?.status === HttpStatusCode.Created;

/**
 * One agreement: an always-visible header naming the document, a one-line description, and the
 * document itself behind a disclosure.
 *
 * Collapsed is not hidden. What carries the notice is that the header naming the agreement, and
 * the checkbox accepting it, are on screen at all times and adjacent to the button that completes
 * the signing — which is the posture Meyer v. Uber turned on. The document is one click away for
 * anyone who wants it.
 */
function AgreementDisclosure({
  title,
  description,
  open,
  onToggle,
  children,
  testId,
}: {
  title: string;
  description: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="inline-flex items-center gap-2 font-semibold text-gray-800 underline"
        data-testid={testId}
      >
        {title}
        <CaretDown
          size={14}
          weight="bold"
          className={open ? 'rotate-180' : undefined}
        />
      </button>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      {open && (
        <div className="mt-4 max-h-[400px] overflow-y-auto border border-gray-300 bg-gray-50 p-6">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * The TACH Master Services Agreement, including Exhibit A, on one application step.
 *
 * ONE document: BluePenguin supplies its terms to be "embedded as an amendment or exhibit" to the
 * reseller's own customer agreement, so the exhibit sits at the end of the MSA the way it would in
 * a paper contract. Nothing on the surface names a second company.
 *
 * TWO consents: BluePenguin requires the signer to "initial or separately sign" the exhibit and
 * states that embedding alone is not sufficient, so accepting the MSA must never carry the exhibit
 * with it. One adopted signature, two checkboxes, two signature records with their own versions.
 * Do not fold the checkboxes into one.
 *
 * Neither agreement is gated on being scrolled or opened. Enforceability of online terms turns on
 * reasonable notice plus an affirmative manifestation of assent, not on proof of reading — a
 * conspicuous, named disclosure plus an "I have read and agree" checkbox is materially stronger
 * than the presentation upheld in Meyer v. Uber, which had neither a scroll gate nor a checkbox.
 * What evidences the transaction is the signature record itself: version, timestamp and
 * server-stamped IP pin the exact document that was presented, which a scroll event never did.
 *
 * Design: tach-architecture/docs/combined-agreements-step-feature-spec.md
 */
function Agreements({
  currStep,
  selectedRole,
  setStep,
  application,
}: AgreementsProps) {
  const { callProtectedApi } = useApi();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<AgreementsFormData>({
    defaultValues: {
      signatureFullName: '',
      msaChecked: false,
      bankChecked: false,
    },
  });
  const { userRecord, userId } = useUser();
  const [loading, setLoading] = useState(false);
  const { screenView } = useScreenView();
  const [msaOpen, setMsaOpen] = useState(false);
  // The footer's tooltip is prop-driven and the old components never passed these, so a customer
  // with Continue disabled got no explanation at all. It is the only cue now that the scroll and
  // open gates are gone and the remaining reasons — no name, unchecked boxes — are silent ones.
  const [showTooltip, setShowTooltip] = useState(false);

  const msaChecked = watch('msaChecked');
  const bankChecked = watch('bankChecked');
  const signatureFullName = watch('signatureFullName');

  // The primary owner, captured back on the personal-info step. ESIGN/UETA treat a signature as
  // one "executed OR adopted ... with the intent to sign", so a prefilled name is a valid
  // signature once adopted — which is what the checkboxes below now say in as many words. The
  // field stays editable on purpose: the person completing onboarding is not always the owner,
  // and an editable prefill they can correct is a better record than a locked one.
  const owner = (userRecord as CustomerWithDriverOnboarding | null)?.driver;
  const prefilledSignature = [owner?.first_name, owner?.last_name]
    .filter(Boolean)
    .join(' ');

  const isSubmitDisabled =
    !signatureFullName || !msaChecked || !bankChecked || loading;

  // Fills an empty field only, so a name the customer has corrected is never overwritten. Keyed
  // on the owner name because userRecord is not necessarily hydrated on the first render.
  useEffect(() => {
    if (prefilledSignature && !getValues('signatureFullName')) {
      setValue('signatureFullName', prefilledSignature);
    }
  }, [prefilledSignature, getValues, setValue]);

  const onSubmit: SubmitHandler<AgreementsFormData> = async (data) => {
    try {
      setLoading(true);
      // One signing act, so one timestamp across both records.
      const signedAt = new Date().toISOString();

      // Sent in parallel: each handler writes a disjoint column set (msa_* vs bank_agreement_*)
      // on Customers, the model sets timestamps: false so there is no shared updated_at, and
      // optimistic locking is off — so the two saves cannot clobber each other. Running them in
      // series would only serialize two Lambda cold starts.
      //
      // Payloads are built field by field rather than spread from `data`: both joi schemas are
      // .unknown(false).strict(true), so leaking msaChecked/bankChecked into either body is a 400.
      //
      // hideAlert on both, because a shared outage fails both calls and would otherwise stack two
      // identical toasts; one message is raised below instead.
      const [msaResponse, bankResponse] = await Promise.all([
        callProtectedApi(
          `/customers/${userId}/sign-msa`,
          'POST',
          {
            signatureFullName: data.signatureFullName,
            agreementChecked: data.msaChecked,
            signedAt,
            version: MSA_VERSION,
          },
          'Error saving your agreements.',
          undefined,
          undefined,
          0,
          true
        ),
        callProtectedApi(
          `/customers/${userId}/sign-bank-agreement`,
          'POST',
          {
            signatureFullName: data.signatureFullName,
            agreementChecked: data.bankChecked,
            signedAt,
            version: BANK_AGREEMENT_VERSION,
          },
          'Error saving your agreements.',
          undefined,
          undefined,
          0,
          true
        ),
      ]);

      // callProtectedApi resolves with the error response (or null) instead of throwing, so the
      // result has to be inspected. Advancing on a failed signature would strand the customer
      // past a step whose agreement was never recorded — the bank signature in particular gates
      // the BluePenguin agreement PDF that gets sent on activation. Staying put is safe: a retry
      // re-posts both, and overwriting an already-recorded signature is harmless.
      if (!wasSigned(msaResponse) || !wasSigned(bankResponse)) {
        toast.error(SAVE_FAILED_MESSAGE);
        return;
      }

      // The prequal_decision step was removed from the flow, so signing advances straight to
      // assets. The prequalification run is unaffected: it is started asynchronously by the
      // Highway lookup on the transition INTO this step, and nothing downstream gated on the
      // decision — the old screen advanced to assets on approval and rejection alike.
      const nextStep = await setApplicationStep({
        step: ApplicationStep.Assets,
        application_id: application?.application_id,
        customer_id: userId ?? '',
        callProtectedApi,
        setLoading,
        previousStep: ApplicationStep.MasterServiceAgreement,
        applicationStatus: application?.status as ApplicationStatus,
      });
      if (nextStep === ApplicationStep.Assets) {
        setStep(ApplicationStep.Assets);
      }
    } catch (error) {
      // Not unreachable: callProtectedApi only RESOLVES error responses for request failures — it
      // THROWS NotAuthenticatedError on a lapsed Auth0 session and AuthTokenError on an Auth0
      // outage, and Promise.all propagates either. Without this the spinner would appear, vanish,
      // and leave the customer looking at an unchanged page with no idea why.
      console.error('Error saving agreements:', error);
      toast.error(SAVE_FAILED_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  // One form body for every breakpoint; only widths and type scale differ, which is all the two
  // hand-maintained copies in the old MasterServiceAgreement/BankAgreement components varied by.
  const renderForm = (isMobile: boolean) => (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={isMobile ? 'w-[95%] space-y-6' : 'w-[70%] space-y-6'}
    >
      {/* One document. BluePenguin supplies its language to be "embedded as an amendment or
      exhibit" to the reseller's own customer agreement, so the exhibit sits at the end of the MSA
      the way it would in a paper contract — not as a second agreement beside it. The two texts
      stay separate modules because they are separately versioned (MSA 3.3, exhibit 1.0) and take
      separate consents; only the presentation is continuous. */}
      <AgreementDisclosure
        title="Master Services Agreement"
        description="Membership and any applicable fees, the 30-day money-back guarantee, cancellation, and the banking terms in Exhibit A."
        open={msaOpen}
        onToggle={() => setMsaOpen((open) => !open)}
        testId="msa-disclosure"
      >
        <MsaContractText businessName={(userRecord as any)?.business_name} />
        <div className="mt-10 border-t border-gray-300 pt-8">
          <p className="mb-4 text-center text-sm font-bold tracking-widest text-gray-600">
            EXHIBIT A
          </p>
          <BankAgreementText />
        </div>
      </AgreementDisclosure>

      {/* Signature first, then the two assents — each checkbox adopts "the name above", so the
      field it refers to has to precede them. */}
      <div className="space-y-4 border-t pt-6">
        <div>
          <label
            htmlFor="signatureFullName"
            className="block text-sm font-medium text-gray-700"
          >
            Full Legal Name <span className="text-red-500">*</span>
          </label>
          <Controller
            name="signatureFullName"
            control={control}
            rules={{
              required: 'Full legal name is required',
              minLength: {
                value: 2,
                message: 'Name must be at least 2 characters',
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                id="signatureFullName"
                placeholder="John Doe"
                status={errors.signatureFullName ? 'error' : ''}
                className="mt-1 h-[38px]"
                style={{ borderRadius: 0 }}
              />
            )}
          />
          {errors.signatureFullName && (
            <p className="mt-1 text-sm text-red-500">
              {errors.signatureFullName.message}
            </p>
          )}
          {/* Folds in what used to be a separate "Electronic Signature" heading and paragraph.
          The second sentence matters with a prefilled name: whoever is at the keyboard has to be
          able to tell that they can correct it. */}
          <p
            className={`mt-2 text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'}`}
          >
            Checking the boxes below adopts this name as your signature, with
            the same legal effect as signing by hand. Edit it if you are signing
            under a different name.
          </p>
        </div>

        <div>
          <Controller
            name="msaChecked"
            control={control}
            rules={{ required: 'You must agree to the terms to continue' }}
            render={({ field }) => (
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className={isMobile ? 'text-xs' : 'text-sm'}
                data-testid="msa-checkbox"
              >
                <span className="text-gray-700">
                  I have read and agree to the{' '}
                  <span className="font-semibold">
                    Master Services Agreement
                  </span>
                  , and adopt the name above as my electronic signature.{' '}
                  <span className="text-red-500">*</span>
                </span>
              </Checkbox>
            )}
          />
          {errors.msaChecked && (
            <p className="mt-1 text-sm text-red-500">
              {errors.msaChecked.message}
            </p>
          )}
        </div>

        {/* A second, discrete act — not folded into the checkbox above. BluePenguin requires the
        signer to "initial or separately sign" the exhibit and says embedding it is not sufficient,
        so one combined assent would not evidence what the bank needs. The trailing clause says why
        a second box exists at all, without naming the partners on the surface. */}
        <div>
          <Controller
            name="bankChecked"
            control={control}
            rules={{ required: 'You must accept the exhibit to continue' }}
            render={({ field }) => (
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className={isMobile ? 'text-xs' : 'text-sm'}
                data-testid="bank-checkbox"
              >
                <span className="text-gray-700">
                  I also consent to{' '}
                  <span className="font-semibold">
                    Exhibit A — Banking Services
                  </span>
                  , as required by our banking partners.{' '}
                  <span className="text-red-500">*</span>
                </span>
              </Checkbox>
            )}
          />
          {errors.bankChecked && (
            <p className="mt-1 text-sm text-red-500">
              {errors.bankChecked.message}
            </p>
          )}
        </div>
      </div>

      {/* No onBackClick: this step has never had back navigation. SignupFooter's case for it
      renders Continue only, so the prop the old components passed was never wired to anything. */}
      <SignupFooter
        selectedRole={selectedRole}
        currStep={currStep}
        disableSubmit={isSubmitDisabled}
        isMobile={isMobile}
        showTooltip={showTooltip}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      />
    </form>
  );

  const renderScreenView = (screenView: string) => {
    const isMobile = screenView === 'mobile';
    return (
      <div className="flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle">
        {!loading ? (
          <>
            <div
              className={`mb-6 flex flex-col items-center ${
                isMobile ? 'w-[95%]' : 'w-[70%]'
              }`}
            >
              <h2
                className={`text-center font-bold ${
                  isMobile ? 'text-[24px]' : 'text-[30px]'
                }`}
              >
                Your TACH Agreement
              </h2>
              <p
                className={`text-center text-gray-500 ${
                  isMobile ? 'text-sm' : ''
                }`}
              >
                Review and sign to continue.
              </p>
            </div>
            {userRecord && userId && userId?.length > 0 ? (
              renderForm(isMobile)
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={true}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full justify-center"
              />
            )}
          </>
        ) : (
          <ClipLoader
            color="#1D213B"
            loading={loading}
            size={60}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="h-full w-full items-center justify-center"
          />
        )}
      </div>
    );
  };

  return <>{renderScreenView(screenView)}</>;
}

export default Agreements;
